import { z } from 'zod';
import {
  AgreementStatus,
  CandidateSource,
  DocLang,
  DomainEntityType,
  FileKind,
  GoDecision,
  MarginLevel,
  OutcomeReason,
  PartnerType,
  PoolStatus,
  PortalStatus,
  PortalStatusDetail,
  PositionStage,
  ProjectType,
  RateUnit,
  SubmissionStatus,
  WaitingOn,
  WorkModel,
} from '../enums';
import {
  languageEntrySchema,
  listQueryBase,
  marginOverrideSchema,
  optionalDate,
  optionalEmail,
  optionalNumber,
  optionalText,
  refSchema,
  requiredText,
  stringArray,
} from './common.schema';

// ---------------------------------------------------------------------------
// Partners
// ---------------------------------------------------------------------------

export const partnerCreateSchema = z.object({
  name: requiredText.max(120),
  type: PartnerType.input,
  shortCode: optionalText,
  contactPerson: optionalText,
  email: optionalText,
  phone: optionalText,
  agreement: AgreementStatus.input.optional(),
  terms: optionalText,
  notes: optionalText,
  externalRef: optionalText,
});
export type PartnerCreate = z.infer<typeof partnerCreateSchema>;

export const partnerUpdateSchema = partnerCreateSchema.partial();
export type PartnerUpdate = z.infer<typeof partnerUpdateSchema>;

export const partnerListQuerySchema = listQueryBase.extend({
  type: PartnerType.input.optional(),
});

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------

const descriptionObject = z.object({
  context: z.string().default(''),
  responsibilities: stringArray,
  mustHave: stringArray,
  niceToHave: stringArray,
  notes: z.string().default(''),
});

/** Accepts snake_case keys (must_have, nice_to_have) as well, as agents tend to send them. */
function normalizeDescriptionKeys(v: unknown): unknown {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
  const o = v as Record<string, unknown>;
  const out: Record<string, unknown> = { ...o };
  if ('must_have' in o && !('mustHave' in o)) out.mustHave = o.must_have;
  if ('nice_to_have' in o && !('niceToHave' in o)) out.niceToHave = o.nice_to_have;
  delete out.must_have;
  delete out.nice_to_have;
  return out;
}

export const positionDescriptionSchema = z.preprocess(normalizeDescriptionKeys, descriptionObject);
/** Partial description for updates. */
export const positionDescriptionPatchSchema = z.preprocess(
  normalizeDescriptionKeys,
  descriptionObject.partial(),
);
export type PositionDescription = z.infer<typeof positionDescriptionSchema>;

export const positionCreateSchema = z.object({
  role: requiredText.max(200),
  requestNo: requiredText.max(40),
  /** Partner name, short code, UUID or external ref. */
  intermediary: refSchema.optional(),
  stage: PositionStage.input.optional(),
  department: optionalText,
  workModel: WorkModel.input.nullable().optional(),
  languages: stringArray.optional(),
  startDate: optionalDate,
  endDate: optionalDate,
  submissionDeadline: optionalDate,
  clientRateEur: optionalNumber,
  clientRateUnit: RateUnit.input.optional(),
  clientRateNotes: optionalText,
  marginPct: marginOverrideSchema,
  goDecision: GoDecision.input.optional(),
  goReason: optionalText,
  portalStatus: PortalStatus.input.optional(),
  portalStatusDetail: PortalStatusDetail.input.optional(),
  portalCheckedAt: optionalDate,
  portalSnapshot: z.record(z.unknown()).nullable().optional(),
  projectType: ProjectType.input.nullable().optional(),
  workload: optionalText,
  location: optionalText,
  clientInternalName: optionalText,
  restrictions: optionalText,
  description: positionDescriptionPatchSchema.optional(),
  seats: z.number().int().min(1).nullable().optional(),
  onSiteFlag: z.boolean().optional(),
  jdReference: optionalText,
  externalRef: optionalText,
  /** Contact person at the intermediary for this request; empty = the partner's contact applies. */
  contactName: optionalText,
  contactEmail: optionalEmail,
  contactPhone: optionalText,
  createdAt: z.string().datetime().optional(),
  /**
   * User id of the team member responsible for the role. Omitted on create = the acting user
   * (when the caller is one); `null` = nobody.
   */
  ownerId: z.string().uuid().nullable().optional(),
});
export type PositionCreate = z.infer<typeof positionCreateSchema>;

/** `intermediary: null` clears the intermediary. */
export const positionUpdateSchema = positionCreateSchema
  .partial()
  .extend({ intermediary: refSchema.nullable().optional() });
export type PositionUpdate = z.infer<typeof positionUpdateSchema>;

export const positionListQuerySchema = listQueryBase.extend({
  stage: z.union([PositionStage.input, z.array(PositionStage.input)]).optional(),
  portalStatus: PortalStatus.input.optional(),
  openOnly: z.coerce.boolean().optional(),
  urgentOnly: z.coerce.boolean().optional(),
  requestNo: z.string().optional(),
  intermediary: z.string().optional(),
  goDecision: GoDecision.input.optional(),
  /** Positions owned by this user id. */
  ownerId: z.string().uuid().optional(),
});
export type PositionListQuery = z.infer<typeof positionListQuerySchema>;

export const setPositionStageSchema = z.object({
  stage: PositionStage.input,
  reason: optionalText,
  /** Decisions for open submissions when moving to Lost/Dropped (spec §6.1). */
  submissionDecisions: z
    .array(
      z.object({
        submissionId: refSchema,
        status: SubmissionStatus.input.nullable(),
        outcomeReason: OutcomeReason.input.nullable().optional(),
      }),
    )
    .optional(),
  /** Apply the proposed defaults for Screening submissions (Rejected / Client closed the role). */
  applyProposedDefaults: z.boolean().optional(),
});
export type SetPositionStage = z.infer<typeof setPositionStageSchema>;

export const recordPortalCheckSchema = z.object({
  requestNo: requiredText,
  status: PortalStatusDetail.input,
  checkedAt: optionalDate,
  note: optionalText,
  intermediary: refSchema.optional(),
});
export type RecordPortalCheck = z.infer<typeof recordPortalCheckSchema>;

export const upsertPositionFromPortalSchema = z.object({
  requestNo: requiredText,
  intermediary: refSchema.default('TechProjectsNow'),
  role: optionalText,
  suggestedRole: optionalText,
  portal: z.record(z.unknown()).optional(),
  portalStatus: PortalStatusDetail.input.optional(),
  portalCheckedAt: optionalDate,
  description: positionDescriptionPatchSchema.optional(),
  descriptionText: optionalText,
  workModel: WorkModel.input.nullable().optional(),
  workload: optionalText,
  location: optionalText,
  projectType: ProjectType.input.nullable().optional(),
  languages: stringArray.optional(),
  startDate: optionalDate,
  endDate: optionalDate,
  submissionDeadline: optionalDate,
  maxRate: optionalNumber,
  maxRateUnit: RateUnit.input.optional(),
  showRates: z.boolean().optional(),
  currentLowestRate: optionalNumber,
  goDecision: GoDecision.input.optional(),
  goReason: optionalText,
  restrictions: optionalText,
  clientInternalName: optionalText,
  seats: z.number().int().min(1).nullable().optional(),
  onSiteFlag: z.boolean().optional(),
  createdOnPortal: optionalDate,
  /** Contact person named on the portal; only fills position contact fields that are still empty. */
  contactName: optionalText,
  contactEmail: optionalEmail,
  contactPhone: optionalText,
});
export type UpsertPositionFromPortal = z.infer<typeof upsertPositionFromPortalSchema>;

// ---------------------------------------------------------------------------
// Candidates
// ---------------------------------------------------------------------------

export const candidateCreateSchema = z.object({
  fullName: optionalText,
  label: optionalText,
  headline: optionalText,
  skills: stringArray.optional(),
  languages: z.array(languageEntrySchema).optional(),
  country: optionalText,
  city: optionalText,
  email: optionalText,
  phone: optionalText,
  rateExpectationText: optionalText,
  rateExpectationEurH: optionalNumber,
  /** Convenience: rate + unit → normalised to rateExpectationEurH. */
  rateExpectation: optionalNumber,
  rateExpectationUnit: RateUnit.input.optional(),
  availableFrom: optionalDate,
  source: CandidateSource.input.nullable().optional(),
  /** Partner name, short code, UUID or external ref. */
  supplier: refSchema.nullable().optional(),
  poolStatus: PoolStatus.input.optional(),
  consentDate: optionalDate,
  summary: optionalText,
  /** Role used for the Drive folder name when the name is unknown. */
  role: optionalText,
  candidateId: z
    .string()
    .regex(/^DC-C-\d{4}-\d{4,}$/i)
    .optional(),
  externalRef: optionalText,
  createdAt: z.string().datetime().optional(),
});
export type CandidateCreate = z.infer<typeof candidateCreateSchema>;

export const candidateUpdateSchema = candidateCreateSchema.omit({ candidateId: true }).partial();
export type CandidateUpdate = z.infer<typeof candidateUpdateSchema>;

export const candidateListQuerySchema = listQueryBase.extend({
  poolStatus: z.union([PoolStatus.input, z.array(PoolStatus.input)]).optional(),
  skill: z.string().optional(),
  language: z.string().optional(),
  supplier: z.string().optional(),
  country: z.string().optional(),
});
export type CandidateListQuery = z.infer<typeof candidateListQuerySchema>;

// ---------------------------------------------------------------------------
// Submissions
// ---------------------------------------------------------------------------

export const submissionCreateSchema = z.object({
  candidate: refSchema,
  position: refSchema,
  status: SubmissionStatus.input.optional(),
  outcomeReason: OutcomeReason.input.nullable().optional(),
  candidateRateEur: optionalNumber,
  candidateRateUnit: RateUnit.input.optional(),
  candidateRateNotes: optionalText,
  clientRateEur: optionalNumber,
  clientRateNotes: optionalText,
  marginPct: marginOverrideSchema,
  sentOn: optionalDate,
  feedbackDue: optionalDate,
  rtrReceived: z.boolean().optional(),
  rtrDate: optionalDate,
  waitingOn: WaitingOn.input.nullable().optional(),
  nextStep: optionalText,
  followUpOn: optionalDate,
  notes: optionalText,
  externalRef: optionalText,
  createdAt: z.string().datetime().optional(),
});
export type SubmissionCreate = z.infer<typeof submissionCreateSchema>;

export const submissionUpdateSchema = submissionCreateSchema
  .omit({ candidate: true, position: true, status: true })
  .partial();
export type SubmissionUpdate = z.infer<typeof submissionUpdateSchema>;

export const setSubmissionStatusSchema = z.object({
  status: SubmissionStatus.input,
  outcomeReason: OutcomeReason.input.nullable().optional(),
  sentOn: optionalDate,
  note: optionalText,
});
export type SetSubmissionStatus = z.infer<typeof setSubmissionStatusSchema>;

export const submissionListQuerySchema = listQueryBase.extend({
  status: z.union([SubmissionStatus.input, z.array(SubmissionStatus.input)]).optional(),
  position: z.string().optional(),
  candidate: z.string().optional(),
  waitingOn: WaitingOn.input.optional(),
  needsChase: z.coerce.boolean().optional(),
  chaseList: z.coerce.boolean().optional(),
});
export type SubmissionListQuery = z.infer<typeof submissionListQuerySchema>;

// ---------------------------------------------------------------------------
// Margin
// ---------------------------------------------------------------------------

export const setMarginSchema = z.object({
  level: MarginLevel.input,
  id: refSchema.optional(),
  marginPct: z.union([z.number(), z.null()]),
  reason: optionalText,
});
export type SetMargin = z.infer<typeof setMarginSchema>;

// ---------------------------------------------------------------------------
// Activity, follow-ups, briefings
// ---------------------------------------------------------------------------

export const activityCreateSchema = z.object({
  entityType: DomainEntityType.input,
  entityId: refSchema,
  text: requiredText.max(4000),
  date: optionalDate,
  externalRef: optionalText,
});
export type ActivityCreate = z.infer<typeof activityCreateSchema>;

export const followUpCreateSchema = z.object({
  entityType: DomainEntityType.input,
  entityId: refSchema,
  text: requiredText.max(1000),
  dueOn: optionalDate,
  done: z.boolean().optional(),
  externalRef: optionalText,
});
export type FollowUpCreate = z.infer<typeof followUpCreateSchema>;

export const followUpUpdateSchema = z.object({
  text: requiredText.max(1000).optional(),
  dueOn: optionalDate,
  done: z.boolean().optional(),
});

export const jdBriefingCreateSchema = z.object({
  position: refSchema,
  partner: refSchema,
  jdVersion: z.union([z.string(), z.number()]).optional(),
  sentOn: optionalDate,
  notes: optionalText,
  externalRef: optionalText,
});
export type JdBriefingCreate = z.infer<typeof jdBriefingCreateSchema>;

export const stopSourcingSchema = z.object({
  position: refSchema,
  partner: refSchema.optional(),
  date: optionalDate,
});
export type StopSourcing = z.infer<typeof stopSourcingSchema>;

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

export const fileRegisterSchema = z.object({
  entityType: DomainEntityType.input,
  entityId: refSchema,
  kind: FileKind.input.default('OTHER'),
  driveFileId: optionalText,
  driveUrl: optionalText,
  jdVersion: z.union([z.string(), z.number()]).optional(),
  profileVersion: z.union([z.string(), z.number()]).optional(),
  externalRef: optionalText,
});
export type FileRegister = z.infer<typeof fileRegisterSchema>;

export const fileUploadSchema = z.object({
  entityType: DomainEntityType.input,
  entityId: refSchema,
  kind: FileKind.input.default('OTHER'),
  fileName: requiredText,
  base64: z.string().min(1),
  mimeType: optionalText,
});
export type FileUpload = z.infer<typeof fileUploadSchema>;

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export const generateJdSchema = z.object({
  position: refSchema,
  lang: DocLang.input.default('EN'),
  content: z.unknown(),
  changeNote: optionalText,
  force: z.boolean().optional(),
  forceReason: optionalText,
  /** Admin override of the on-site block (spec §6.1). */
  overrideOnSiteReason: optionalText,
});
export type GenerateJd = z.infer<typeof generateJdSchema>;

export const generateProfileSchema = z.object({
  candidate: refSchema,
  position: refSchema.optional(),
  lang: DocLang.input.default('EN'),
  content: z.unknown(),
  changeNote: optionalText,
  force: z.boolean().optional(),
  forceReason: optionalText,
  /** Admin override allowing one page more than PROFILE_MAX_PAGES. */
  allowExtraPage: z.boolean().optional(),
});
export type GenerateProfile = z.infer<typeof generateProfileSchema>;

export const renderMessageSchema = z.object({
  templateKey: requiredText,
  submission: refSchema.optional(),
  position: refSchema.optional(),
  candidate: refSchema.optional(),
  partner: refSchema.optional(),
  overrides: z.record(z.unknown()).optional(),
});
export type RenderMessage = z.infer<typeof renderMessageSchema>;

export const calculateRatesSchema = z.object({
  clientRate: optionalNumber,
  candidateRate: optionalNumber,
  unit: RateUnit.input.optional(),
  clientRateUnit: RateUnit.input.optional(),
  candidateRateUnit: RateUnit.input.optional(),
  marginPct: z.number().optional(),
  position: refSchema.optional(),
  submission: refSchema.optional(),
  lang: DocLang.input.optional(),
});
export type CalculateRatesInput = z.infer<typeof calculateRatesSchema>;

// ---------------------------------------------------------------------------
// Saved views
// ---------------------------------------------------------------------------

export const savedViewSchema = z.object({
  entityType: DomainEntityType.input,
  name: requiredText.max(80),
  layout: z.enum(['TABLE', 'BOARD']).default('TABLE'),
  filters: z.record(z.unknown()).default({}),
  sorts: z.array(z.object({ id: z.string(), desc: z.boolean().default(false) })).default([]),
  columns: z.array(z.string()).default([]),
  groupBy: optionalText,
  isDefault: z.boolean().optional(),
  shared: z.boolean().optional(),
});
export type SavedViewInput = z.infer<typeof savedViewSchema>;
